// ============================================================
// THE MASS STRUCK (plan body-map H1; the goo study's hits, ruled
// 2026-10-08). The mass has no lines to recolour: a hit changes its body,
// every term fading with the hit's age -
//
//   Explosive  knocked away from the blow, its drops flung far
//   Impact     squashed flat and wide, then ringing back
//   Energy     a crater where it struck (the drops there pulled in and
//              shrunk), the core shrinking, boiling, steam rising off it
//   Rending    torn in two, strands stretched between (three on a critical)
//
// - a critical the same term doubled and slower (its age runs at half
// speed), and the mass knocked too. Applied to the moving body
// (gooLive.gooBody) after its conditions; its drops in one fixed order for
// the plate to draw (`massDrops`).
//
// Pure. Zero React dependency. The study's frame; seconds.
// ============================================================

import type { DamageCode } from '../impacts';
import type { Drop } from './goo';
import { bell, satAngle, type GooBody } from './gooLive';
import { landed, type BurstHit } from './burst';
import { seeded } from './hash';

/** One hit on the mass: its type, the side it came from (radians), its weight (2 a critical), seconds since. */
export interface GooHit {
  type: DamageCode;
  ang: number;
  amp: number;
  age: number;
}

/** The mass as struck: also the strands between its torn halves, and whether it boils. */
export interface StruckGoo extends GooBody {
  bridges: Drop[];
  boil: boolean;
}

/** How long a hit moves the mass, in its own (weighted) seconds. */
const LIFE = 2.6;
const STRANDS = 9;
const CORES = 3;

/** A hit's weighted age, or null once it is over (or not yet begun). */
function liveAge(h: GooHit): number | null {
  const a = h.age / h.amp;
  return a >= 0 && a <= LIFE ? a : null;
}

/** The smallest turn between two angles, signed. */
const turn = (a: number, b: number) => Math.atan2(Math.sin(a - b), Math.cos(a - b));
const rise = (a: number, k: number) => Math.sin(Math.min(Math.PI, a * k));

/** What a hit does to the core: knocked, squashed, its size, boiling, torn. */
interface CoreTerms {
  kx: number;
  ky: number;
  squash: number;
  size: number;
  boil: boolean;
  tear: { part: number; ang: number; three: boolean } | null;
}

/** Each type's own term on the core (a critical's knock is apart, in `knock`). */
const CORE_TERMS: Record<DamageCode, (h: GooHit, a: number, t: number, c: CoreTerms) => void> = {
  X: () => {},
  I: (h, a, _t, c) => {
    c.squash += 0.42 * h.amp * Math.exp(-2.2 * a) * Math.cos(7 * a);
  },
  E: (h, a, t, c) => {
    c.boil = true;
    c.size *= (1 + 0.06 * Math.sin(22 * t) * Math.exp(-a)) * (h.amp > 1 ? 1 - 0.2 * Math.exp(-0.7 * a) : 1);
  },
  R: (h, a, _t, c) => {
    c.tear = { part: 95 * (h.amp > 1 ? 1.5 : 1) * rise(a, 1.25), ang: h.ang, three: h.amp > 1 };
  },
};

/** Explosive, or any critical: the mass knocked away from the side the blow came from. */
function knock(h: GooHit, a: number, c: CoreTerms) {
  if (h.type !== 'X' && h.amp <= 1) return;
  const k = (h.type === 'X' ? 34 : 22) * h.amp * Math.exp(-4 * a) * Math.sin(Math.min(Math.PI, a * 9));
  c.kx -= k * Math.cos(h.ang);
  c.ky -= k * Math.sin(h.ang) * 0.6;
}

function coreTerms(hits: readonly GooHit[], t: number): CoreTerms {
  const c: CoreTerms = { kx: 0, ky: 0, squash: 0, size: 1, boil: false, tear: null };
  for (const h of hits) {
    const a = liveAge(h);
    if (a === null) continue;
    knock(h, a, c);
    CORE_TERMS[h.type](h, a, t, c);
  }
  return c;
}

/** Each type's own push on a satellite at angle `base` (how much farther out, how much smaller), near or far. */
const SAT_TERMS: Partial<Record<DamageCode, (h: GooHit, a: number, base: number, near: number) => [number, number]>> = {
  I: (h, a, base, near) => [
    (-60 * Math.exp(-4 * a) + 40 * Math.sin(9 * a) * Math.exp(-2 * a)) * near * h.amp +
      (h.amp > 1 ? 150 * rise(a, 1.6) * Math.abs(Math.cos(base)) : 0),
    1,
  ],
  X: (h, a, _base, near) => [260 * h.amp * 0.75 * rise(a, 1.3) * (h.amp > 1 ? 1 : 0.4 + near), 1],
  E: (h, a, _base, near) => [-115 * h.amp * Math.exp(-1.8 * a) * near, 1 - 0.7 * near * Math.exp(-1.2 * a)],
};

/** A satellite's push out (frame units) and its size, from every live hit. */
function satTerms(hits: readonly GooHit[], base: number): [number, number] {
  let out = 0;
  let size = 1;
  for (const h of hits) {
    const a = liveAge(h);
    const term = SAT_TERMS[h.type];
    if (a === null || !term) continue;
    const [d, k] = term(h, a, base, bell(turn(base, h.ang), 0.6));
    out += d;
    size *= k;
  }
  return [out, size];
}

const shift = ([x, y, r]: Drop, dx: number, dy: number): Drop => [x + dx, y + dy, r];

/** Each satellite pushed along its own angle, as the body places it (stretched when dead, flattened). */
function pushSats(body: GooBody, hits: readonly GooHit[], ti: number, dead: number): Drop[] {
  return body.sats.map(([x, y, r], i) => {
    const base = satAngle(i, ti);
    const [d, k] = satTerms(hits, base);
    const out = d * (1 + 0.5 * dead);
    return [x + out * Math.cos(base), y + out * Math.sin(base) * (1.15 - 0.95 * dead), r * k];
  });
}

/** The tear's halves (or thirds) round the core, the strands between them, and each satellite carried with its part. */
function tearApart(b: StruckGoo, tear: NonNullable<CoreTerms['tear']>, dead: number): StruckGoo {
  const n = tear.three ? 3 : 2;
  const angles = Array.from({ length: n }, (_, k) => tear.ang + Math.PI / 2 + (k * 2 * Math.PI) / n);
  const r = b.cr * (n === 3 ? 0.78 : 0.9) * (1 - 0.35 * dead);
  const cores: Drop[] = angles.map((th) => [
    b.cx + tear.part * Math.cos(th),
    b.cy + tear.part * Math.sin(th) * (1 - dead),
    r,
  ]);
  const strand = Math.max(0, 34 - tear.part * 0.45);
  const per = n === 3 ? 3 : 7;
  const bridges = cores.slice(0, n === 2 ? 1 : n).flatMap(([ax, ay], k) => {
    const [bx, by] = cores[(k + 1) % n];
    return Array.from({ length: per }, (_, j): Drop => {
      const f = (j + 1) / (per + 1);
      return [ax + (bx - ax) * f, ay + (by - ay) * f, strand * (1 - 0.5 * Math.sin(Math.PI * f))];
    });
  });
  const sats = b.sats.map((d, i) => {
    const base = Math.atan2(d[1] - b.cy, d[0] - b.cx) || i;
    const th = angles.reduce((best, a) => (Math.cos(base - a) > Math.cos(base - best) ? a : best));
    return shift(d, tear.part * Math.cos(th), tear.part * Math.sin(th) * (1 - dead));
  });
  return { ...b, cores, bridges, sats };
}

/** Squashed: wider and flatter, standing on the same ground. */
function squashed(b: StruckGoo, sq: number): StruckGoo {
  if (!sq) return b;
  const ground = b.cy + b.cr;
  const S = ([x, y, r]: Drop): Drop => [b.cx + (x - b.cx) * (1 + sq), ground - (ground - y) * (1 - sq), r];
  return { ...b, cores: b.cores.map(S), bridges: b.bridges.map(S), sats: b.sats.map(S) };
}

/** Padded to `n` with drops of nothing at the core: the plate draws a fixed set. */
const padded = (drops: Drop[], n: number, at: [number, number]): Drop[] => [
  ...drops,
  ...Array.from({ length: Math.max(0, n - drops.length) }, (): Drop => [at[0], at[1], 0]),
];

/**
 * The mass as these hits leave it: its body (gooLive) knocked, squashed, cratered, torn - every term fading with
 * its hit's age. `ti` is the mass's own clock, `t` real time, `dead` Dead's eased weight. No live hit: the body as it
 * was, padded.
 */
export function struckGoo(body: GooBody, hits: readonly GooHit[], ti: number, t: number, dead: number): StruckGoo {
  const c = coreTerms(hits, t);
  const sats = pushSats(body, hits, ti, dead).map((d) => shift(d, c.kx, c.ky));
  const cx = body.cx + c.kx;
  const cy = body.cy + c.ky;
  const cores = body.cores.map(([x, y, r]): Drop => [x + c.kx, y + c.ky, r * c.size]);
  let b: StruckGoo = { ...body, cx, cy, cr: body.cr * c.size, cores, sats, bridges: [], boil: c.boil };
  if (c.tear && c.tear.part > 0) b = tearApart(b, c.tear, dead);
  b = squashed(b, c.squash);
  return { ...b, cores: padded(b.cores, CORES, [cx, cy]), bridges: padded(b.bridges, STRANDS, [cx, cy]) };
}

/** The settled mass's drops (`gooAtRest`, or a still body) in the plate's order: no tear, no strands. */
export function settledDrops(b: { cores: readonly Drop[]; sats: readonly Drop[] }): Drop[] {
  const [cx, cy] = b.cores[0];
  return massDrops({
    cores: padded([...b.cores], CORES, [cx, cy]),
    bridges: padded([], STRANDS, [cx, cy]),
    sats: [...b.sats],
  });
}

/** Every drop the mass draws, in one fixed order: three cores, nine strands, nine satellites. */
export const massDrops = (b: { cores: readonly Drop[]; bridges: readonly Drop[]; sats: readonly Drop[] }): Drop[] => [
  ...b.cores,
  ...b.bridges,
  ...b.sats,
];

/**
 * A burst's hits on the mass, `elapsed` seconds in: each from its own side (which the attack decides) and its own
 * moment in turn (not yet begun, it waits). A field's block is not a hit on the body.
 */
export function gooHitsOf(hits: readonly BurstHit[], id: string, elapsed: number): GooHit[] {
  return landed(hits).map((h, k) => ({
    type: h.type,
    amp: h.crit ? 2 : 1,
    age: elapsed - h.at,
    ang: seeded(id)(k) * 2 * Math.PI,
  }));
}

/**
 * Seconds until a burst's last landed hit stops moving the mass (its life, doubled for a critical; the steam's is
 * shorter): the play must last this long, or the mass snaps back mid-move. 0 with none landed.
 */
export function gooSpan(hits: readonly BurstHit[]): number {
  return Math.max(0, ...landed(hits).map((h) => h.at + LIFE * (h.crit ? 2 : 1)));
}

/** One wisp of Energy's steam. */
export interface Spark {
  x: number;
  y: number;
  r: number;
  opacity: number;
}

/** The steam's slots: twelve a hit, twenty-four for a critical. */
const SPARKS = 24;
const STEAM_LIFE = 2.4;

/** Energy's steam rising off the side it struck, `SPARKS` slots (unused ones unseen). */
export function gooSparks(hits: readonly GooHit[], body: Pick<GooBody, 'cx' | 'cy' | 'cr'>): Spark[] {
  // The latest Energy hit begun (the youngest): a burst's steam goes on while any of them is still live.
  const h = hits.filter((x) => x.type === 'E' && x.age >= 0).sort((a, b) => a.age - b.age)[0];
  const a = h ? h.age / h.amp : -1;
  const n = h ? 12 * h.amp : 0;
  return Array.from({ length: SPARKS }, (_, k) => {
    if (!h || k >= n || a < 0 || a > STEAM_LIFE) return { x: body.cx, y: body.cy, r: 0, opacity: 0 };
    const sa = h.ang + (k / n - 0.5) * 1.3;
    const sx = body.cx + Math.cos(sa) * (body.cr + 8);
    const sy = body.cy + Math.sin(sa) * (body.cr + 8) * 1.1;
    return {
      x: sx + ((k % 3) - 1) * 22 * a + 14 * Math.sin(3 * a + k),
      y: sy - (60 + 40 * (k % 4)) * a,
      r: 3 + (k % 3) * 2 * (1 + a),
      opacity: Math.max(0, 1 - a / 2.2) * 0.8,
    };
  });
}
